/**
 * F17 T17.11.5 — admin API client dla szablonów procesów.
 *
 * Woła /api/admin/process-templates (admin scope — cookie session, bez Bearer).
 * Wzorzec per-domena: named async functions, discriminated union dla błędów.
 */

import { PROCESS_GRAPH_SCHEMA_VERSION } from '@saas/shared';

import type { ApproverConfigValue } from '@/components/process-builder/ApproverPicker';
import type {
  ConditionConfig,
  ConditionConfigByClass,
} from '@/components/process-builder/ConditionNode';
import { api } from '@/lib/api';
import type { ProcessEdgeType } from '@/lib/processes/constants';

// ============================================================================
// Domain types
// ============================================================================

export interface ProcessTemplateRecord {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  targetModuleCode: string;
  version: number;
  isPublished: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateGraphNodeInput {
  clientId: string;
  nodeType: 'start' | 'decision' | 'parallel_split' | 'parallel_join' | 'end';
  label?: string;
  approverConfig?: ApproverConfigValue;
  /** „Pozwól cofnąć do poprzedniego etapu" — tylko etap decyzji; brak klucza = `false`. */
  allowStepBack?: boolean;
  // Pola wymagane żyją na drodze (`TemplateGraphEdgeInput.requiredFields`), nie na węźle.
  positionX: number;
  positionY: number;
}

export interface TemplateGraphEdgeInput {
  sourceClientId: string;
  targetClientId: string;
  edgeType: ProcessEdgeType;
  conditionConfig?: ConditionConfig | ConditionConfigByClass;
  label?: string;
  /** Kolejność dróg w obrębie etapu źródłowego (od 0 per etap). */
  sortOrder?: number;
  /**
   * Droga domyślna etapu: jedna zwykła albo kilka warunkowych, bez mieszania (pilnuje publikacja);
   * przy kilku widocznych obowiązuje pierwsza wg kolejności.
   */
  isDefault?: boolean;
  /** Wybór drogi wymaga komentarza. */
  requireComment?: boolean;
  /** Pola wymagane do wybrania drogi (klucz = klasa dokumentu albo `"default"`). */
  requiredFields?: Record<string, string[]> | null;
}

export interface TemplateBlueprintBody {
  nodes: TemplateGraphNodeInput[];
  edges: TemplateGraphEdgeInput[];
}

export interface ProcessTemplateWithBlueprint extends ProcessTemplateRecord {
  draftBlueprint: TemplateBlueprintBody | null;
  publishedBlueprint: TemplateBlueprintBody | null;
}

// ============================================================================
// List
// ============================================================================

export interface ListTemplatesQuery {
  q?: string;
  isPublished?: boolean;
  targetModuleCode?: string;
  limit?: number;
  offset?: number;
}

export interface ListTemplatesResponse {
  data: ProcessTemplateRecord[];
  total: number;
  limit: number;
  offset: number;
}

/** GET /api/admin/process-templates */
export async function listAdminProcessTemplates(
  query: ListTemplatesQuery = {},
): Promise<ListTemplatesResponse> {
  const params: Record<string, string> = {};
  if (query.q) params.q = query.q;
  if (query.isPublished !== undefined) params.isPublished = String(query.isPublished);
  if (query.targetModuleCode) params.targetModuleCode = query.targetModuleCode;
  if (query.limit !== undefined) params.limit = String(query.limit);
  if (query.offset !== undefined) params.offset = String(query.offset);
  const res = await api.get<ListTemplatesResponse>('/admin/process-templates', { params });
  return res.data;
}

// ============================================================================
// Get single
// ============================================================================

/** GET /api/admin/process-templates/:id */
export async function getAdminProcessTemplate(id: string): Promise<ProcessTemplateWithBlueprint> {
  const res = await api.get<ProcessTemplateWithBlueprint>(`/admin/process-templates/${id}`);
  return res.data;
}

// ============================================================================
// Create
// ============================================================================

export interface CreateTemplateInput {
  name: string;
  slug: string;
  description?: string | null;
  targetModuleCode: string;
}

/** POST /api/admin/process-templates */
export async function createAdminProcessTemplate(
  input: CreateTemplateInput,
): Promise<ProcessTemplateRecord> {
  const res = await api.post<ProcessTemplateRecord>('/admin/process-templates', input);
  return res.data;
}

// ============================================================================
// Patch metadata
// ============================================================================

export interface PatchTemplateInput {
  name?: string;
  description?: string | null;
}

/** PATCH /api/admin/process-templates/:id */
export async function patchAdminProcessTemplate(
  id: string,
  input: PatchTemplateInput,
): Promise<ProcessTemplateRecord> {
  const res = await api.patch<ProcessTemplateRecord>(`/admin/process-templates/${id}`, input);
  return res.data;
}

// ============================================================================
// Patch blueprint (graph save)
// ============================================================================

export interface PatchBlueprintResponse {
  nodeIdMap: Record<string, string>;
  nodeCount: number;
  edgeCount: number;
}

/** PATCH /api/admin/process-templates/:id/blueprint */
export async function patchAdminTemplateBlueprintRaw(
  id: string,
  body: TemplateBlueprintBody,
): Promise<PatchBlueprintResponse> {
  // Wersja kształtu zapisu — serwer odrzuca zapis z nieodświeżonej karty (409 GRAPH_SCHEMA_OUTDATED).
  const res = await api.patch<PatchBlueprintResponse>(`/admin/process-templates/${id}/blueprint`, {
    graphSchemaVersion: PROCESS_GRAPH_SCHEMA_VERSION,
    ...body,
  });
  return res.data;
}

// ============================================================================
// Publish
// ============================================================================

export interface PublishTemplateSuccess {
  ok: true;
  template: ProcessTemplateRecord;
}

export interface PublishTemplateFailure {
  ok: false;
  error: 'GRAPH_VALIDATION_FAILED' | 'ALREADY_PUBLISHED' | 'UNKNOWN';
  /**
   * Kody odrzucenia publikacji szablonu: reguły grafu jak przy procesie (`PublishIssue`, w tym
   * reguły etapów i dróg) oraz reguły samego szablonu (np. `NON_RELATIVE_APPROVER`,
   * `UNSUPPORTED_TARGET_MODULE`) — dlatego `string[]`; tłumaczenia pod
   * `tenant.processes.builder.publishCheck.issues.<kod>`.
   */
  issues?: string[];
}

/** POST /api/admin/process-templates/:id/publish */
export async function publishAdminProcessTemplate(
  id: string,
): Promise<PublishTemplateSuccess | PublishTemplateFailure> {
  try {
    const res = await api.post<ProcessTemplateRecord>(`/admin/process-templates/${id}/publish`);
    return { ok: true, template: res.data };
  } catch (err: unknown) {
    const ax = err as {
      response?: { status?: number; data?: { error?: string; issues?: string[] } };
    };
    const status = ax.response?.status;
    const data = ax.response?.data;
    // Trasa publikacji szablonu odpowiada 422 `TEMPLATE_VALIDATION_FAILED` (walidacja blueprintu);
    // `GRAPH_VALIDATION_FAILED` zostaje przyjmowane dla zgodności z trasą procesu.
    if (
      status === 422 &&
      (data?.error === 'TEMPLATE_VALIDATION_FAILED' || data?.error === 'GRAPH_VALIDATION_FAILED')
    ) {
      return { ok: false, error: 'GRAPH_VALIDATION_FAILED', issues: data.issues ?? [] };
    }
    if (status === 409 && data?.error === 'ALREADY_PUBLISHED') {
      return { ok: false, error: 'ALREADY_PUBLISHED' };
    }
    return { ok: false, error: 'UNKNOWN' };
  }
}

// ============================================================================
// Delete (soft)
// ============================================================================

export interface DeleteTemplateSuccess {
  ok: true;
}

export interface DeleteTemplateFailure {
  ok: false;
  error: 'ALREADY_DELETED' | 'TEMPLATE_NOT_FOUND' | 'UNKNOWN';
}

/** DELETE /api/admin/process-templates/:id */
export async function deleteAdminProcessTemplate(
  id: string,
): Promise<DeleteTemplateSuccess | DeleteTemplateFailure> {
  try {
    await api.delete(`/admin/process-templates/${id}`);
    return { ok: true };
  } catch (err: unknown) {
    const ax = err as {
      response?: { status?: number; data?: { error?: string } };
    };
    const status = ax.response?.status;
    const data = ax.response?.data;
    if (status === 409 && data?.error === 'ALREADY_DELETED') {
      return { ok: false, error: 'ALREADY_DELETED' };
    }
    if (status === 404 && data?.error === 'TEMPLATE_NOT_FOUND') {
      return { ok: false, error: 'TEMPLATE_NOT_FOUND' };
    }
    return { ok: false, error: 'UNKNOWN' };
  }
}
